// Utility to format mathematical expressions for display

// Unicode superscript characters
const superscriptMap = {
  '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
  '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
  '+': '⁺', '-': '⁻', '=': '⁼', '(': '⁽', ')': '⁾',
  'n': 'ⁿ', 'i': 'ⁱ', 'x': 'ˣ', 'y': 'ʸ', 'a': 'ᵃ',
  'b': 'ᵇ', 'c': 'ᶜ', 'd': 'ᵈ', 'e': 'ᵉ', 'f': 'ᶠ',
  'g': 'ᵍ', 'h': 'ʰ', 'j': 'ʲ', 'k': 'ᵏ', 'l': 'ˡ',
  'm': 'ᵐ', 'o': 'ᵒ', 'p': 'ᵖ', 'r': 'ʳ', 's': 'ˢ',
  't': 'ᵗ', 'u': 'ᵘ', 'v': 'ᵛ', 'w': 'ʷ', 'z': 'ᶻ'
};

// Unicode subscript characters
const subscriptMap = {
  '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄',
  '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉',
  '+': '₊', '-': '₋', '=': '₌', '(': '₍', ')': '₎',
  'a': 'ₐ', 'e': 'ₑ', 'i': 'ᵢ', 'n': 'ₙ', 'x': 'ₓ',
  'o': 'ₒ', 'r': 'ᵣ', 's': 'ₛ', 't': 'ₜ', 'u': 'ᵤ',
  'v': 'ᵥ', 'h': 'ₕ', 'k': 'ₖ', 'l': 'ₗ', 'm': 'ₘ',
  'p': 'ₚ', 'j': 'ⱼ'
};

// Convert a string to superscript
const toSuperscript = (str) => {
  return str.split('').map(char => superscriptMap[char] || char).join('');
};

// Convert a string to subscript
const toSubscript = (str) => {
  return str.split('').map(char => subscriptMap[char] || char).join('');
};

// Format mathematical expressions in text
export const formatMathText = (text) => {
  if (!text) return text;
  
  let formatted = text;
  
  // Handle negative exponents first: x^-1, x^-2, 3x^-1, etc.
  // This pattern catches: letter/number followed by ^- and digits
  formatted = formatted.replace(/(\w)\^(-\d+)/g, (match, base, exp) => {
    return base + toSuperscript(exp);
  });
  
  // Handle parenthesized exponents like ^(n+1), ^(-1), ^(2n)
  formatted = formatted.replace(/(\w)\^(\([^)]+\))/g, (match, base, exp) => {
    const expContent = exp.slice(1, -1); // Remove parentheses
    return base + toSuperscript(expContent);
  });
  
  // Handle multi-character exponents like x^10, x^12
  formatted = formatted.replace(/(\w)\^(\d{2,})/g, (match, base, exp) => {
    return base + toSuperscript(exp);
  });
  
  // Handle variable exponents like x^n, x^m, a^b
  formatted = formatted.replace(/(\w)\^([a-zA-Z])/g, (match, base, exp) => {
    return base + toSuperscript(exp);
  });
  
  // Handle single digit exponents like x^2, x^3
  formatted = formatted.replace(/(\w)\^(\d)/g, (match, base, exp) => {
    return base + toSuperscript(exp);
  });
  
  // Handle subscripts with parentheses: x_(n+1)
  formatted = formatted.replace(/(\w)_(\([^)]+\))/g, (match, base, sub) => {
    const subContent = sub.slice(1, -1);
    return base + toSubscript(subContent);
  });
  
  // Handle subscripts: x_1, a_n, etc.
  formatted = formatted.replace(/(\w)_(\d+)/g, (match, base, sub) => {
    return base + toSubscript(sub);
  });
  
  formatted = formatted.replace(/(\w)_([a-zA-Z])/g, (match, base, sub) => {
    return base + toSubscript(sub);
  });
  
  // Handle common math symbols
  formatted = formatted.replace(/\bsqrt\(([^)]+)\)/gi, '√($1)');
  formatted = formatted.replace(/\bpi\b/gi, 'π');
  formatted = formatted.replace(/\binfinity\b/gi, '∞');
  formatted = formatted.replace(/\btheta\b/gi, 'θ');
  formatted = formatted.replace(/\balpha\b/gi, 'α');
  formatted = formatted.replace(/\bbeta\b/gi, 'β');
  formatted = formatted.replace(/\bdelta\b/gi, 'Δ');
  formatted = formatted.replace(/\bsigma\b/gi, 'Σ');
  formatted = formatted.replace(/\blambda\b/gi, 'λ');
  formatted = formatted.replace(/!=|<>/g, '≠');
  formatted = formatted.replace(/<=/g, '≤');
  formatted = formatted.replace(/>=/g, '≥');
  formatted = formatted.replace(/\+-/g, '±');
  formatted = formatted.replace(/\*\*/g, '×');
  
  return formatted;
};

export default formatMathText;
